import React, { useState, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { setSolution } from "../../store/actions/solution";
import { setLoadingRoutesList } from "../../store/actions/loading";
import { setCurrentDriver, toggleRoute } from "../../store/actions/map";
import { handleRouteLock } from "../../store/actions/custom";
import { withSnackbar } from "notistack";
import API from "../../utils/axios";
import { Box, Grid, makeStyles } from "@material-ui/core";
import {
  checkIsFOJW,
  isNotEmptyRoute,
  isSearchingValueMatch
} from "../../utils/helpers";
import { getRouteListStatus } from "../../utils/routeListStatus";
import { getLastCustomerEta } from "../../utils/routeTime";
import { client } from "../map/DriverMarkers";
import RoutesToolbar from "./RoutesToolbar";
import UnscheduledBar from "./UnscheduledBar";
import RouteDetails from "./RouteDetails";
import RouteStatusFilter from "./RouteStatusFilter";
import {
  setShowingRouteId,
  toggleRouteExpandedStatus,
  toggleRouteSelectedStatus
} from "../../store/actions/project";
import {
  scrollToStop,
  setProjectTabValue,
  setRoutesSearchMode
} from "../../store/actions/common";
import SearchBoxMessage from "../common/SearchBoxMessage";
import useListKeyboardCommands from "../project/useListKeyboardCommands";
import { useFirebaseRemoteConfig } from "../../states/firebase";
import { setLockedMatchStop } from "../../store/actions/common";
import { distancePointToPolyline } from "../../utils/geo";

const useStyles = makeStyles(theme => ({
  listWrapper: ({ noBottomActions }) => ({
    backgroundColor: "#fff",
    paddingBottom: noBottomActions ? 0 : 140,
    "& + &": {
      borderTop: theme.variables.border
    }
  }),
  routesListBody: {
    boxSizing: "border-box"
  },
  fontWeightMedium: {
    fontWeight: 500
  },
  searchBoxMessage: {
    paddingTop: theme.spacing(3)
  },
  routeStatusEdge: {
    transition: "opacity .15s ease, background-color .15s ease",
    "&.status-inactive": {
      borderLeft: `4px solid ${theme.palette.secondary.main}`
    },
    "&.status-active": {
      borderLeft: `4px solid ${theme.palette.primary.main}`
    },
    "&.status-finished": {
      borderLeft: `4px solid ${theme.palette.success.main}`
    },
    "&.status-warning": {
      borderLeft: `4px solid ${theme.palette.error.main}`
    },
    // FOJW contextual matching: while an unscheduled stop is hovered/selected, the
    // route carrying its RouteTag stays lit and the rest fade. (See computeActiveMatchSig.)
    "&.route-match": {
      backgroundColor: "#F4F7FF"
    },
    "&.route-dim": {
      opacity: 0.34
    }
  }
  // stopPlaceholderTop: {
  //   height: theme.spacing(2),
  //   marginBottom: theme.spacing(0.5),
  //   width: "80%",
  //   backgroundColor: "rgba(0,0,0,.15)"
  // },
  // stopPlaceholderBottom: {
  //   height: theme.spacing(3),
  //   width: "85%",
  //   marginBottom: theme.spacing(1.75),
  //   backgroundColor: "rgba(0,0,0,.15)"
  // },
  // stopPlaceholderRect: {
  //   width: theme.spacing(4),
  //   height: theme.spacing(4),
  //   backgroundColor: "rgba(0,0,0,.15)",
  //   flexShrink: 0,
  //   marginBottom: theme.spacing(2),
  //   borderRadius: "4px"
  // }
  // stopSPlaceholderBottom: {
  //   height: theme.spacing(3),
  //   width: "85%",
  //   marginBottom: theme.spacing(1.75),
  //   backgroundColor: "rgba(0,0,0,.15)"
  // },
}));

// Current wall-clock time as seconds-of-day (0–86399), matching stop.arr_time's units and
// the convention in updateStopFromWebsocket (project.js). Drives the time-based "warning".
const secondsOfDayNow = () => {
  const d = new Date();
  return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
};

// Distinct DRIVER-tag ("RouteTag") names across a backhaul group's stops.
const getRouteDriverTagNames = backhaulGroup => {
  const names = new Set();
  backhaulGroup.forEach(route =>
    (route.stops || []).forEach(stop =>
      (stop.tags || []).forEach(tag => {
        if (tag && tag.type === "DRIVER" && tag.name) names.add(tag.name);
      })
    )
  );
  return names;
};

// DRIVER-tag names on a single stop.
const getStopDriverTagNames = stop => {
  const names = new Set();
  (stop && stop.tags ? stop.tags : []).forEach(tag => {
    if (tag && tag.type === "DRIVER" && tag.name) names.add(tag.name);
  });
  return names;
};

const getUnassignedStops = state =>
  (state.project.solution &&
    state.project.solution.unassigned &&
    state.project.solution.unassigned.stops) ||
  [];

// The route's approximate path for proximity ranking: its real customer stops in
// visit order (excluding depot start/end and breaks — matching allNonStartEndBreakStops).
const getCustomerStopPath = backhaulGroup => {
  const mainRoute = backhaulGroup[0];
  return (mainRoute.stops || [])
    .filter(
      s => s.type !== "start" && s.type !== "end" && s.type !== "break"
    )
    .map(s => s.address)
    .filter(a => a);
};

// FOJW contextual RouteTag: a sorted, joined signature of the DRIVER-tag names driving
// the current match — but ONLY while the Unscheduled panel is open. A *pinned* (locked)
// stop wins outright (stable while the mouse roams the routes list); otherwise the
// hovered / selected unscheduled stop(s). Returns "" otherwise, so the routes list does
// NOT re-render on unrelated hover/selection changes (this runs in mapStateToProps).
const computeActiveMatchSig = state => {
  if (!checkIsFOJW()) return "";
  if (!state.extendable || state.extendable.type !== "unscheduled") return "";

  const lockedId = state.common.lockedMatchStopId;
  if (lockedId) {
    const locked = getUnassignedStops(state).find(s => s.id === lockedId);
    if (locked) {
      return Array.from(getStopDriverTagNames(locked))
        .sort()
        .join(",");
    }
  }

  const names = new Set();
  const add = stop =>
    getStopDriverTagNames(stop).forEach(n => names.add(n));
  const hovered = state.map.hoverStop && state.map.hoverStop.stop;
  if (hovered) add(hovered);
  const ds = state.common.dragSelection;
  if (ds && ds.listId === "unassigned" && ds.list && ds.list.length) {
    const selected = new Set(ds.list);
    getUnassignedStops(state).forEach(stop => {
      if (selected.has(stop.id)) add(stop);
    });
  }
  return Array.from(names)
    .sort()
    .join(",");
};

// The single unscheduled stop that drives the "matches to the top, closest first"
// reorder — the pinned stop wins, otherwise the hovered one (multi-select has no
// single stop, so it highlights in place without reordering). Its id is a stable
// primitive so the routes list only reorders when the active stop changes.
const computeActiveMatchStopId = state => {
  if (!checkIsFOJW()) return null;
  if (!state.extendable || state.extendable.type !== "unscheduled") return null;
  const lockedId = state.common.lockedMatchStopId;
  if (lockedId) {
    const locked = getUnassignedStops(state).find(s => s.id === lockedId);
    if (locked && getStopDriverTagNames(locked).size > 0) return lockedId;
  }
  const hovered = state.map.hoverStop && state.map.hoverStop.stop;
  if (hovered && getStopDriverTagNames(hovered).size > 0) return hovered.id;
  return null;
};

function RouteGroups({
  projectId,
  solution,
  selectedRoutes,
  handleRouteLock,
  setSolution,
  setLoadingRoutesList,
  warnReopen,
  handleReoptimizeSelectedRoutes,
  toggleRouteSelectedStatus,
  toggleRoute,
  isPastProject,
  published,
  shareRoute,
  setRoutesSearchMode,
  isRoutesSearchMode,
  setProjectTabValue,
  setShowingRouteId,
  activeMatchSig,
  activeMatchStopId,
  lockedMatchStopId,
  unscheduledPanelOpen,
  setLockedMatchStop
  // projectLoadingInitial
}) {
  const classes = useStyles({
    noBottomActions:
      (selectedRoutes && selectedRoutes.length > 0) || isRoutesSearchMode
  });
  const [searchInputValue, setSearchInputValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [nowSeconds, setNowSeconds] = useState(() => secondsOfDayNow());
  const [haveHovered, setHaveHovered] = useState(false);
  const listKeyboardCommands = useListKeyboardCommands();

  // FOJW contextual RouteTag: the tag names to match right now (from the hovered /
  // selected unscheduled stop). Gated by the remote flag; [] when nothing is active.
  const firebaseRemoteConfig = useFirebaseRemoteConfig();
  const activeMatchTags = useMemo(() => {
    const flagOn =
      firebaseRemoteConfig.getString("ft_routetag_in_planner") !== "false";
    return flagOn && activeMatchSig ? activeMatchSig.split(",") : [];
  }, [firebaseRemoteConfig, activeMatchSig]);

  const unassigned = solution ? solution.unassigned.stops : [];

  // Pressing Esc unpins a locked RouteTag match.
  useEffect(() => {
    if (!lockedMatchStopId) return undefined;
    const onKey = e => {
      if (e.key === "Escape") setLockedMatchStop(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lockedMatchStopId, setLockedMatchStop]);

  // Drop the pin if the Unscheduled panel is closed, or the pinned stop is no
  // longer unscheduled (it got assigned, or the project reloaded) — so a stale
  // lock can't keep dimming/reordering the list.
  useEffect(() => {
    if (!lockedMatchStopId) return;
    if (
      !unscheduledPanelOpen ||
      !unassigned.some(s => s.id === lockedMatchStopId)
    ) {
      setLockedMatchStop(null);
    }
  }, [
    lockedMatchStopId,
    unscheduledPanelOpen,
    unassigned,
    setLockedMatchStop
  ]);

  // Share route
  // Can resend
  const canResend = useCallback(sentAt => {
    const now = Date.now();
    const sentDate = new Date(sentAt).getTime();

    return now > sentDate + 60000;
  }, []);

  const resendToDriver = useCallback(
    (e, id, sentAt) => {
      e.stopPropagation();

      if (canResend(sentAt)) {
        API.post(`/v1/projects/${projectId}/routes/${id}/send`)
          .then(() => {
            const newSolution = { ...solution };
            const routeIndex = newSolution.routes.findIndex(el => el.id === id);

            newSolution.routes[routeIndex].message_sent_at = new Date();
            setSolution(newSolution);
          })
          .catch(error => console.log(error));
      }
    },
    [canResend, solution, setSolution, projectId]
  );

  // Route groups helpers

  // const hasRouteSelected = shift => {
  //   return (
  //     solution.showAll ||
  //     solution.routeGroups[shift].some(backhaulGroup =>
  //       backhaulGroup.some(isRouteSelected)
  //     )
  //   );
  // };

  // const notEmptyShift = shift => {
  //   return (
  //     solution.showAll ||
  //     solution.routeGroups[shift].some(routes =>
  //       routes.every(route => isNotEmptyRoute(route))
  //     )
  //   );
  // };

  // const getVehiclesQuantity = groupId =>
  //   solution.routeGroups[groupId].filter(
  //     group => group[0] && isRouteSelected(group[0])
  //   ).length;

  // Remove driver from route
  const removeDriver = useCallback(
    id => {
      setLoadingRoutesList(true);
      const newSolution = { ...solution };

      const routeIndex = solution.routes.findIndex(el => el.id === id);
      newSolution.routes[routeIndex].driver = null;

      setSolution(newSolution);

      API.put(`/v1/projects/${projectId}/routes/${id}/drivers`, {
        driver_id: null
      })
        .then(() => {
          client &&
            client.send(
              JSON.stringify({
                type: "driver_change",
                route_id: newSolution.routes[routeIndex].id,
                driver_id: null
              })
            );
        })
        .catch(error => console.log(error))
        .finally(() => {
          setLoadingRoutesList(false);
        });
    },
    [projectId, setLoadingRoutesList, solution, setSolution]
  );

  // Show driver marker
  // const [driverIndex, setDriverIndex] = useState(0);
  // const showDriver = useCallback(
  //   backhaulGroup => e => {
  //     e.stopPropagation();

  //     if (backhaulGroup[driverIndex]) {
  //       const routeId = backhaulGroup[driverIndex].id;
  //       const length = backhaulGroup.length;

  //       setCurrentDriver(routeId);
  //       setTimeout(() => setCurrentDriver(null), 1000);

  //       setDriverIndex((driverIndex + 1) % length);
  //     }
  //   },
  //   [driverIndex, setCurrentDriver]
  // );

  useEffect(() => {
    return () => {
      setRoutesSearchMode(false);
    };
  }, []);

  // Tick a clock every 60s so a route can flip into the time-based "warning" state on its
  // own (no Redux change). Paused on past projects, where a live warning is meaningless.
  useEffect(() => {
    if (isPastProject) return undefined;
    setNowSeconds(secondsOfDayNow());
    const id = setInterval(() => setNowSeconds(secondsOfDayNow()), 60000);
    return () => clearInterval(id);
  }, [isPastProject]);

  // Time-based "warning" routes (stalled past next-stop ETA + grace). Exposed as a
  // value-stable signature so the 60s tick re-evaluates membership WITHOUT giving
  // routeGroupsToShow / statusCounts a new identity every minute (which would reset the
  // keyboard list + scroll via the effects below). `ids` = warning route ids; `sig` = their
  // sorted join, a primitive that only changes value when membership actually changes.
  const warningNow = isPastProject ? null : nowSeconds;
  const warning = useMemo(() => {
    const keys = Object.keys(solution?.routeGroups || {});
    const groups =
      keys.length > 0
        ? solution.routeGroups[keys[0]].filter(bg => bg.some(isNotEmptyRoute))
        : [];
    const ids = new Set();
    groups.forEach(bg => {
      if (getRouteListStatus(bg[0], warningNow) === "warning") {
        ids.add(bg[0].id);
      }
    });
    return { ids, sig: Array.from(ids).sort().join(",") };
  }, [solution.routeGroups, warningNow]);

  const routeGroupsToShow = useMemo(
    () =>
      Object.keys(solution?.routeGroups).length > 0
        ? solution?.routeGroups[Object.keys(solution?.routeGroups)[0]].filter(
            backhaulGroup => {
              const isRoutesNotEmpty = !backhaulGroup.some(route =>
                isNotEmptyRoute(route)
              );
              if (isRoutesNotEmpty) {
                return false;
              }

              if (statusFilter !== "all") {
                const status = warning.ids.has(backhaulGroup[0].id)
                  ? "warning"
                  : getRouteListStatus(backhaulGroup[0]);
                if (status !== statusFilter) {
                  return false;
                }
              }

              const driverName = `
                ${backhaulGroup[0]?.driver?.first_name}${backhaulGroup[0]?.driver?.last_name}`;
              const vehicleName = backhaulGroup[0].vehicle_name;

              const isSearchingDriverNameMatch = isSearchingValueMatch(
                driverName,
                searchInputValue
              );
              const isSearchingVehicleNameMatch = isSearchingValueMatch(
                vehicleName,
                searchInputValue
              );

              const isSearchingRouteMatch =
                isSearchingDriverNameMatch || isSearchingVehicleNameMatch;
              const isValidSearchingValue =
                searchInputValue !== "" && backhaulGroup[0];

              return isValidSearchingValue ? isSearchingRouteMatch : true;
            }
          )
          // FOJW: keep the routes that need the most attention "top of mind" — sort
          // late → early by the ETA at the last customer (across the whole backhaul group,
          // matching the value the card displays). No last-customer ETA sinks to the bottom.
          .sort((a, b) => {
            const etaA = getLastCustomerEta(a.map(r => r.stops).flat());
            const etaB = getLastCustomerEta(b.map(r => r.stops).flat());
            return (
              (etaB === null ? -Infinity : etaB) -
              (etaA === null ? -Infinity : etaA)
            );
          })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchInputValue, statusFilter, solution.routeGroups, warning.sig]
  );

  // The unscheduled stop currently driving the match — hovered, or pinned. Its
  // matching routes are pulled to the top; proximity is measured from its coords.
  const activeStop = useMemo(
    () =>
      activeMatchStopId
        ? unassigned.find(s => s.id === activeMatchStopId) || null
        : null,
    [activeMatchStopId, unassigned]
  );

  // While a stop is hovered or pinned, pull its matching routes to the TOP,
  // closest-passing first (direct distance from the stop to each route's customer
  // path), so the dispatcher never scrolls to find them. Non-matches keep their
  // existing order below. The reorder is on the routes list (left) while the
  // cursor is on the stops list (right), so it doesn't jump under the pointer.
  const displayGroups = useMemo(() => {
    if (
      routeGroupsToShow === null ||
      !activeStop ||
      !activeStop.address ||
      activeMatchTags.length === 0
    ) {
      return routeGroupsToShow;
    }
    const tagSet = new Set(activeMatchTags);
    const decorated = routeGroupsToShow.map((backhaulGroup, i) => {
      const names = getRouteDriverTagNames(backhaulGroup);
      let isMatch = false;
      tagSet.forEach(t => {
        if (names.has(t)) isMatch = true;
      });
      const dist = isMatch
        ? distancePointToPolyline(
            activeStop.address,
            getCustomerStopPath(backhaulGroup)
          )
        : Infinity;
      return { backhaulGroup, i, isMatch, dist };
    });
    decorated.sort((a, b) => {
      if (a.isMatch !== b.isMatch) return a.isMatch ? -1 : 1;
      if (a.isMatch) return a.dist - b.dist; // matches: closest first
      return a.i - b.i; // non-matches: keep original order
    });
    return decorated.map(d => d.backhaulGroup);
  }, [routeGroupsToShow, activeStop, activeMatchTags]);

  const statusCounts = useMemo(() => {
    const keys = Object.keys(solution?.routeGroups || {});
    const groups =
      keys.length > 0
        ? solution.routeGroups[keys[0]].filter(bg => bg.some(isNotEmptyRoute))
        : [];
    const counts = {
      all: groups.length,
      inactive: 0,
      active: 0,
      finished: 0,
      warning: 0
    };
    groups.forEach(bg => {
      const status = warning.ids.has(bg[0].id)
        ? "warning"
        : getRouteListStatus(bg[0]);
      counts[status] += 1;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solution.routeGroups, warning.sig]);

  // The Warning pill is hidden when its count is 0; if it was the active filter and the last
  // warning route cleared (self-healed), fall back to "all" so the list isn't stuck empty.
  useEffect(() => {
    if (statusFilter === "warning" && !statusCounts.warning) {
      setStatusFilter("all");
    }
  }, [statusFilter, statusCounts.warning]);

  useEffect(() => {
    listKeyboardCommands.methods.reset();
    listKeyboardCommands.methods.setListSize(displayGroups.length);
  }, [displayGroups, listKeyboardCommands.methods]);

  useEffect(() => {
    if (searchInputValue === "") {
      listKeyboardCommands.methods.stop();
    } else {
      listKeyboardCommands.methods.start();
    }
  }, [listKeyboardCommands.methods, searchInputValue]);

  useEffect(() => {
    const element = document.getElementById(
      `route_details_${listKeyboardCommands.currentIndex}`
    );
    if (element) {
      element.scrollIntoView(false);
    }
  }, [listKeyboardCommands.currentIndex]);

  listKeyboardCommands.methods.setEnterHandler((event, index) => {
    setShowingRouteId(displayGroups[index][0].id);
  });

  if (
    routeGroupsToShow === null ||
    (!isRoutesSearchMode && Object.keys(solution?.routeGroups).length === 0)
  ) {
    setProjectTabValue(0);
    return <></>;
  }

  return (
    <>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{ maxHeight: "100%", overflow: "hidden" }}
      >
        <Grid item>
          <RoutesToolbar
            searchInputValue={searchInputValue}
            setSearchInputValue={value => {
              if (value.length > 0) {
                setRoutesSearchMode(true);
              } else {
                setRoutesSearchMode(false);
              }
              setSearchInputValue(value);
            }}
            handleReoptimizeSelectedRoutes={handleReoptimizeSelectedRoutes}
            shareRoute={shareRoute}
            onSearchFocus={() => {
              listKeyboardCommands.methods.enable();
            }}
            onSearchBlur={() => {
              listKeyboardCommands.methods.disable();
            }}
          />
        </Grid>
        {statusCounts.all > 0 && (
          <Grid item>
            <RouteStatusFilter
              value={statusFilter}
              onChange={setStatusFilter}
              counts={statusCounts}
            />
          </Grid>
        )}
        <Grid item style={{ overflow: "auto" }}>
          {routeGroupsToShow.length > 0 && (
            <UnscheduledBar unassigned={unassigned} warnReopen={warnReopen} />
          )}
          <div id="route_groups_wrapper">
            <div className={classes.listWrapper}>
              <Box
                // position="relative"
                className={classes.routesListBody}
              >
                {routeGroupsToShow.length === 0 && searchInputValue !== "" && (
                  <SearchBoxMessage
                    message={`No routes found for "${searchInputValue}"`}
                    className={classes.searchBoxMessage}
                  />
                )}
                {routeGroupsToShow.length === 0 &&
                  searchInputValue === "" &&
                  statusFilter !== "all" && (
                    <SearchBoxMessage
                      message={`No ${statusFilter} routes`}
                      className={classes.searchBoxMessage}
                    />
                  )}
                {displayGroups.length > 0 &&
                  displayGroups.map((backhaulGroup, backhaulIndex) => {
                    const routeId = backhaulGroup[0].id;
                    const routeListStatus = warning.ids.has(routeId)
                      ? "warning"
                      : getRouteListStatus(backhaulGroup[0]);
                    // Contextual match: dim non-matching routes, keep the matching one
                    // lit, only while an unscheduled stop is hovered/selected.
                    let matchClass = "";
                    let matchedTags = null;
                    if (activeMatchTags.length > 0) {
                      const names = getRouteDriverTagNames(backhaulGroup);
                      const matched = activeMatchTags.filter(n => names.has(n));
                      if (matched.length > 0) {
                        matchClass = " route-match";
                        matchedTags = matched;
                      } else {
                        matchClass = " route-dim";
                      }
                    }
                    return (
                      <div
                        key={routeId}
                        className={`${classes.routeStatusEdge} status-${routeListStatus}${matchClass}`}
                      >
                        <RouteDetails
                        index={backhaulIndex}
                        routeId={routeId}
                        backhaulGroup={backhaulGroup}
                        isPastProject={isPastProject}
                        published={published}
                        handleRouteLock={handleRouteLock}
                        toggleRouteSelectedStatus={toggleRouteSelectedStatus}
                        shareRoute={shareRoute}
                        removeDriver={removeDriver}
                        canResend={canResend}
                        resendToDriver={resendToDriver}
                        toggleRoute={toggleRoute}
                        routeIsSelected={selectedRoutes.includes(routeId)}
                        warnReopen={warnReopen}
                        hasSelectedRoutes={selectedRoutes.length > 0}
                        searchInputValue={searchInputValue}
                        activeByListCommand={
                          backhaulIndex === listKeyboardCommands.currentIndex
                        }
                        setHaveHovered={setHaveHovered}
                        haveHovered={haveHovered}
                        matchedTags={matchedTags}
                      />
                      </div>
                    );
                  })}
              </Box>
            </div>

            {/* <ShareRoute
        open={showShare}
        handleClose={handleShareClose}
        routeId={shareRouteId}
      /> */}
          </div>
        </Grid>
      </Grid>
    </>
  ); /* : (
    <Box p={3}>
      {new Array(3).fill("").map((v, i) => (
        <Box display="flex" key={i} alignItems="center">
          <Skeleton variant="rect" className={classes.stopPlaceholderRect} />
          <Box width="100%" flexShrink={1} ml={2}>
            <Skeleton className={classes.stopPlaceholderTop} variant="rect" />
            <Skeleton
              className={classes.stopPlaceholderBottom}
              variant="rect"
            />
          </Box>
        </Box>
      ))}
    </Box>
  ); */
}

const mapStateToProps = state => ({
  userData: state.auth.userData,
  projectId: state.project.id,
  solution: state.project.solution,
  selectedRoutes: state.common.selectedRoutes,
  selectedVehicles: state.project.selectedVehicles,
  emptyRoutesVisible: state.common.emptyRoutesVisible,
  published: ["published", "expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  isPastProject: ["expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  driverDragging: state.common.draggingType === "drivers",
  expandedFromMap: state.common.expandedFromMap,
  expandedRoutes: state.common.expandedRoutes,
  isRoutesSearchMode: state.common.isRoutesSearchMode,
  activeMatchSig: computeActiveMatchSig(state),
  activeMatchStopId: computeActiveMatchStopId(state),
  lockedMatchStopId: state.common.lockedMatchStopId,
  unscheduledPanelOpen: state.extendable.type === "unscheduled"
  // projectLoadingInitial: state.loading.projectInitial
});

export default connect(mapStateToProps, {
  setSolution,
  setLoadingRoutesList,
  handleRouteLock,
  setCurrentDriver,
  toggleRouteExpandedStatus,
  toggleRouteSelectedStatus,
  toggleRoute,
  scrollToStop,
  setRoutesSearchMode,
  setProjectTabValue,
  setShowingRouteId,
  setLockedMatchStop
})(withSnackbar(RouteGroups));
